import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Users, Building2, FileCheck, TrendingUp } from 'lucide-react';

interface DashboardStats {
  totalUsers: number;
  totalCompanies: number;
  pendingFmcsaRequests: number;
  activeLoads: number;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalCompanies: 0,
    pendingFmcsaRequests: 0,
    activeLoads: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [usersRes, companiesRes, fmcsaRes, loadsRes] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('companies').select('id', { count: 'exact', head: true }),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('fmcsa_change_requested', true),
        supabase.from('loads').select('id', { count: 'exact', head: true }).eq('status', 'posted'),
      ]);

      setStats({
        totalUsers: usersRes.count || 0,
        totalCompanies: companiesRes.count || 0,
        pendingFmcsaRequests: fmcsaRes.count || 0,
        activeLoads: loadsRes.count || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Users', value: stats.totalUsers, icon: Users, color: 'text-brand-teal-deep' },
    { label: 'Companies', value: stats.totalCompanies, icon: Building2, color: 'text-emerald-400' },
    { label: 'Pending FMCSA Requests', value: stats.pendingFmcsaRequests, icon: FileCheck, color: 'text-amber-400' },
    { label: 'Active Loads', value: stats.activeLoads, icon: TrendingUp, color: 'text-purple-400' },
  ];

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-[#6b7280] mt-1">Overview of platform activity</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Card key={stat.label} className="bg-[#111820] border-[#1e2530] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6b7280]">{stat.label}</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {loading ? '...' : stat.value.toLocaleString()}
                  </p>
                </div>
                <stat.icon className={`w-8 h-8 ${stat.color} opacity-80`} />
              </div>
            </Card>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
